import { ShimmerPlaceHolder } from "@faire/deprecated-retailer-visitor-shared/components/Animation/ShimmerPlaceHolder";
import { LoadingProductTileSkeleton } from "@faire/deprecated-retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { Core } from "@faire/slate/foundation/color";
import { LoadingSkeleton } from "@faire/web/slate-deprecated/LoadingSkeleton";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import React from "react";
import styled from "styled-components";

const NUM_TILES = 12;

const FILTER_PROPS = {
  height: "40px",
  borderRadius: "20px",
  width: "120px",
};

export const LoggedOutCategoryPageHeroBannerSkeleton: React.FC = () => {
  const titleHeight = useResponsiveValue({
    mobileAndAbove: 32,
    tabletAndAbove: 76,
    desktopAndAbove: 38,
    xLargeAndAbove: 50,
  });

  const titleWidth = useResponsiveValue({
    mobileAndAbove: 330,
    desktopAndAbove: 356,
    xLargeAndAbove: 510,
  });
  return (
    <LoadingWrapper>
      <BannerTextWrapper>
        <StyledPlaceHolder $height={titleHeight} $width={titleWidth} />
        <StyledPlaceHolder $height={26} $width={titleWidth} />
      </BannerTextWrapper>
      <LoadingImgSkeleton />
    </LoadingWrapper>
  );
};

export const LoggedOutCategoryPageLoadingSkeleton: React.FC = () => {
  return (
    <div data-test-id="logged-out-category-page-loading-skeleton">
      <LoggedOutCategoryPageHeroBannerSkeleton />
      <CategoryPageContentLoadingSkeleton />
    </div>
  );
};

// Reusable content section that can be shared with other pages
export const CategoryPageContentLoadingSkeleton: React.FC = () => {
  return (
    <Wrapper>
      {/* carousel header */}
      <LoadingSkeleton width="240px" height={getSpacing("5x")} />
      <Spacer height="2x" />
      {/* carousel loading skeleton */}
      <LoadingSkeleton
        width="100%"
        height={{
          mobileAndAbove: "250px",
          tabletAndAbove: "300px",
          desktopAndAbove: "350px",
        }}
      />
      <Spacer height="2x" />
      {/* All Products header */}
      <LoadingSkeleton width="150px" height={getSpacing("4x")} />
      <Spacer height="2x" />
      <FilterRow>
        <LoadingSkeleton {...FILTER_PROPS} />
        <Spacer width="2x" />
        <LoadingSkeleton {...FILTER_PROPS} />
      </FilterRow>
      <Spacer height="2x" />

      {/* Tiles */}
      <TileGrid>
        {Array.from({ length: NUM_TILES }).map((_, index) => (
          <LoadingProductTileSkeleton key={index} />
        ))}
      </TileGrid>
    </Wrapper>
  );
};

const BannerTextWrapper = styled(Flex).attrs({
  direction: "column",
  justify: "space-between",
  align: "flex-start",
  gap: "3x",
})`
  flex-basis: 50%;
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 672px;
    width: 672px;
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    padding: ${getSpacing(133, "6x")};
  }
  ${responsiveMediaQueries.desktop} {
    padding: ${getSpacing("10x", "6x")};
  }
  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("8x", "4x")};
  }
  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("3x", "2x")};
  }
`;

const LoadingWrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column-reverse", tabletAndAbove: "row" },
  align: "center",
  justify: "center",
})`
  width: 100%;
  background-color: ${Core.surface.tertiary};
  ${responsiveMediaQueries.xxlarge} {
    height: 390px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    height: 290px;
  }
  ${responsiveMediaQueries.tablet} {
    height: 318px;
  }
  ${responsiveMediaQueries.mobile} {
    height: 400px;
  }
`;

const StyledPlaceHolder = styled(ShimmerPlaceHolder)`
  background-color: ${Core.surface.primary};
`;
const LoadingImgSkeleton = styled(ShimmerPlaceHolder)`
  width: 100%;
  height: 100%;
  flex-basis: 50%;
  ${responsiveMediaQueries.xxlarge} {
    flex-basis: 100%;
  }
  background-color: ${Core.surface.secondary};
  ${responsiveMediaQueries.mobile} {
    max-height: 176px;
  }
`;

const TileGrid = styled.div`
  display: grid;
  grid-gap: ${getSpacing("2x")};

  ${responsiveMediaQueries.mobile} {
    grid-template-columns: repeat(2, 2fr);
  }
  ${responsiveMediaQueries.tabletAndAbove} {
    grid-template-columns: repeat(4, 4fr);
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    grid-template-columns: repeat(5, 5fr);
  }
  ${responsiveMediaQueries.xLargeAndAbove} {
    grid-template-columns: repeat(6, 6fr);
  }
`;

const FilterRow = styled(Row)`
  overflow-x: hidden;
`;
const Wrapper = styled(Column)`
  max-width: 1344px;
  margin: 0 auto;

  padding: ${getSpacing("4x", "2x", 0, "2x")};
`;
